import React from "react";
import type { LeaveRequest, LeaveType } from "../../../api/leave/Leave.typed";
import { Box, Card, Chip, Dropdown, IconButton, Menu, MenuButton } from "@mui/joy";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { formatLeaveDurationLabel, formatLeavePeriod } from "../../../utils/leaveHelpers";
import type { LeaveMotifVisibilityOpts } from "../../../utils/planningLeaveHelpers";
import { shouldDisplayLeaveMotifForUi } from "../../../utils/planningLeaveHelpers";
import { formatDate } from "../../../utils/dateTime";
import useAppSettings from "../../../contexts/app/appSettings";
import { CancelIcon, SComment, SInfo } from "../../../styles/StyledMUI";
import {
  ctaAccentColor,
  hrCommentCountChipSx,
  hrMobileRequestCardSx,
  hrTooltipSurfaceSx,
  textTokens,
} from "../../../styles/globalStyles";

interface LeaveRequestListMobileProps {
  requests: LeaveRequest[];
  typesMap: Record<number, LeaveType>;
  canCancelOrDeleteLocal: (request: LeaveRequest) => boolean;
  handleCancelOrDelete: (request: LeaveRequest) => void;
  setOpenCommentsRequestId: (requestId: number) => void;
  renderStatusChip: (status: number) => React.ReactNode;
  leaveMotifVisibilityOpts?: LeaveMotifVisibilityOpts;
}

export default function LeaveRequestListMobile(
  props: LeaveRequestListMobileProps,
): React.ReactNode {
  const [openInfoId, setOpenInfoId] = React.useState<number | null>(null);
  const { config } = useAppSettings();
  const companyTimeZone = config?.TimeZone;

  return (
    <Box sx={{ display: { xs: "block", md: "none" } }}>
      {/* Backdrop: click outside closes the menu (Joy UI does not always handle it in controlled mode) */}
      {openInfoId !== null && (
        <Box
          sx={{ position: "fixed", inset: 0, zIndex: 1299 }}
          onClick={() => setOpenInfoId(null)}
          onTouchStart={() => setOpenInfoId(null)}
          aria-hidden
        />
      )}
      {Array.isArray(props.requests)
        ? props.requests.map((request: LeaveRequest) => (
            <Card key={request.id} sx={hrMobileRequestCardSx}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Dropdown
                    open={openInfoId === request.id}
                    onOpenChange={(_, isOpen) => {
                      setOpenInfoId(isOpen ? request.id : null);
                    }}
                  >
                    <MenuButton
                      slots={{ root: IconButton }}
                      slotProps={{
                        root: {
                          size: "sm",
                          variant: "plain",
                          sx: { color: textTokens.color.dimmed },
                        },
                      }}
                    >
                      <SInfo />
                    </MenuButton>
                    <Menu
                      placement="bottom-start"
                      sx={{ ...hrTooltipSurfaceSx, minWidth: 200, zIndex: 1300 }}
                      onClick={() => setOpenInfoId(null)}
                    >
                      <Box sx={{ px: 1.5, py: 1 }}>
                        <DarkTypography level="body-sm" sx={{ display: "block" }}>
                          Demande : {formatDate(request.created_at)}
                        </DarkTypography>
                        <DarkTypography level="body-sm" sx={{ display: "block" }}>
                          Mise à jour : {formatDate(request.updated_at)}
                        </DarkTypography>
                      </Box>
                    </Menu>
                  </Dropdown>
                  <DarkTypography
                    level="body-sm"
                    sx={{ fontWeight: "bold", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                  >
                    {props.typesMap[request.type_id]?.Name || "—"}
                  </DarkTypography>
                </Box>
                {props.renderStatusChip(request.status)}
              </Box>

              <Box sx={{ mb: 1 }}>
                <DarkTypography level="body-xs" sx={{ color: textTokens.color.faint, mb: 0.5 }}>
                  Période
                </DarkTypography>
                <DarkTypography level="body-sm">
                  {formatLeavePeriod(request, companyTimeZone)}
                </DarkTypography>
              </Box>

              {request.reason &&
                shouldDisplayLeaveMotifForUi(
                  request,
                  Object.values(props.typesMap),
                  props.leaveMotifVisibilityOpts,
                ) && (
                  <Box sx={{ mb: 1 }}>
                    <DarkTypography level="body-xs" sx={{ color: textTokens.color.faint, mb: 0.5 }}>
                      Motif
                    </DarkTypography>
                    <DarkTypography level="body-sm">{request.reason}</DarkTypography>
                  </Box>
                )}

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <DarkTypography level="body-sm" sx={{ fontWeight: "bold" }}>
                  {formatLeaveDurationLabel(request, companyTimeZone)}
                </DarkTypography>

                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Box
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
                    onClick={() => props.setOpenCommentsRequestId(request.id)}
                  >
                    <SComment iconColor={ctaAccentColor} />
                    <Chip size="sm" variant="soft" color="neutral" sx={hrCommentCountChipSx}>
                      {Array.isArray(request.multi_comments) ? request.multi_comments.length : 0}
                    </Chip>
                  </Box>
                  {props.canCancelOrDeleteLocal(request) && (
                    <IconButtonCTA
                      aria-label="Annuler ou supprimer la demande"
                      ctaVariant="danger"
                      size="sm"
                      onClick={() => props.handleCancelOrDelete(request)}
                    >
                      <CancelIcon />
                    </IconButtonCTA>
                  )}
                </Box>
              </Box>
            </Card>
          ))
        : null}
    </Box>
  );
}
